/**
 * App shell — header chrome + tab nav + main content + status footer.
 *
 * Rebuilt in Phase 2 of the frontend redesign. The previous shell had a
 * sidebar, mobile header, bottom tab bar, status bar, and a fixed-position
 * always-pulsing EMERGENCY STOP brick — see the audit summary in
 * docs/plans/2026-04-28-frontend-redesign.md §1 and the new chrome spec
 * in §5.1. The mobile EmergencyStopFab is preserved until Phase 5.
 */

import { useEffect, useState } from 'react'
import { Outlet } from 'react-router-dom'
import { HeaderChrome } from './HeaderChrome'
import { TabNav } from './TabNav'
import { StatusFooter } from './StatusFooter'
import { SkipToContent } from './SkipToContent'
import { EmergencyStopFab } from './EmergencyStopFab'
import { KeyboardNav } from './KeyboardNav'
import { ToastContainer } from '../common/ToastContainer'
import { AlertBanner } from '../common/AlertBanner'
import { ErrorBoundary } from '../common/ErrorBoundary'
import { ReauthDialog, useReauth } from '../common/ReauthDialog'
import { ReceiptDrawer } from '../receipts/ReceiptDrawer'
import { CommandPalette } from '../palette/CommandPalette'
import { useToast } from '../../hooks/useToast'
import { useOnlineStatus } from '../../hooks/useOnlineStatus'
import * as api from '../../api/client'
import { wsClient } from '../../ws'
import { routeMessage } from '../../ws/handlers'
import { useTradingStore } from '../../state/trading'
import type { AutonomyReadiness, MarketDataReadinessSummary, SystemStatus } from '../../types/system'

interface SystemEnvelope {
  systemStatus: SystemStatus
  autonomyReadiness?: AutonomyReadiness
  marketDataReadiness?: MarketDataReadinessSummary
}

export function AppShell() {
  const isOnline = useOnlineStatus()
  const { requireReauth, reauthDialog } = useReauth()
  const { toast } = useToast()
  const [paletteOpen, setPaletteOpen] = useState(false)

  // ⌘K (Mac) / Ctrl+K (others) toggles the command palette.
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault()
        setPaletteOpen((open) => !open)
      }
    }
    document.addEventListener('keydown', handler)
    return () => document.removeEventListener('keydown', handler)
  }, [])

  useEffect(() => {
    const store = useTradingStore.getState()
    const unsubscribeRoute = wsClient.on('*', routeMessage)
    const unsubscribeConnect = wsClient.onConnect(() => {
      wsClient.subscribe(['positions', 'orders', 'risk', 'alerts', 'market_data'])
    })
    const unsubscribeAuthOk = wsClient.on('auth_ok', () => store.setWsConnected(true))
    const unsubscribeAuthFail = wsClient.on('auth_fail', () => store.setWsConnected(false))
    const unsubscribeDisconnect = wsClient.onDisconnect(() => store.setWsConnected(false))
    wsClient.connect()
    return () => {
      unsubscribeRoute()
      unsubscribeConnect()
      unsubscribeAuthOk()
      unsubscribeAuthFail()
      unsubscribeDisconnect()
      wsClient.disconnect()
      store.setWsConnected(false)
    }
  }, [])

  useEffect(() => {
    let cancelled = false
    const refreshSystem = () =>
      api
        .get<SystemEnvelope>('/system')
      .then((envelope) => {
        if (!cancelled) {
          useTradingStore.getState().setSystem({
            ...envelope.systemStatus,
            autonomyReadiness: envelope.autonomyReadiness,
            marketDataReadiness: envelope.marketDataReadiness,
          })
        }
      })
      .catch(() => {
        if (!cancelled) {
          useTradingStore.getState().markSystemUnknown()
        }
      })
    void refreshSystem()
    const interval = window.setInterval(() => {
      void refreshSystem()
    }, 15_000)
    return () => {
      cancelled = true
      window.clearInterval(interval)
    }
  }, [])

  // Mobile FAB still uses requireReauth + emergency-stop endpoint. Replaced
  // with a held-press + the chrome flow in Phase 5.
  const activateMobileEmergencyStop = () => {
    requireReauth(async () => {
      try {
	        await api.post('/system/emergency-stop', { reason: 'operator_mobile_fab' })
	        useTradingStore.getState().markSystemHalted()
	        toast.success(
          'Loop halted. Open orders were not broker-cancelled here; check broker status now.',
        )
      } catch (e) {
        toast.error(e instanceof Error ? e.message : 'Halt failed — please retry.')
      }
    }, 'halt the loop and require manual broker order review')
  }

  return (
    <div className="flex h-screen flex-col bg-ink text-bone">
      <SkipToContent />
      <KeyboardNav />

      {!isOnline && (
        <AlertBanner
          variant="error"
          message="Offline — actions are blocked until connection returns. Data shown is stale."
        />
      )}

      <HeaderChrome />
      <TabNav />

      <main id="main-content" className="flex-1 overflow-y-auto">
        <ErrorBoundary>
          <Outlet />
        </ErrorBoundary>
      </main>

      <StatusFooter />

      {/* Mobile-only kill switch — desktop uses the inline header chrome. */}
      <EmergencyStopFab onConfirm={activateMobileEmergencyStop} />
      <ReauthDialog {...reauthDialog} />
      <ReceiptDrawer />
      <CommandPalette open={paletteOpen} onClose={() => setPaletteOpen(false)} />
      <ToastContainer />
    </div>
  )
}
